import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux'
import {Button, Stack, Typography} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {getLocale, numberToCurrencyString} from "../../Utils/helpers";
import {DateTime} from "luxon";
import {useTranslation} from "react-i18next";
import MoveToWorkQueue from "./MoveToWorkQueue/MoveToWorkQueue";
import BackButton from "./Common/BackButton";
import {ServiceCardType} from "./Types";
import SelectService from './SelectService/SelectService'
import VehicleInformation from './VehicleInformation'
import {RootState} from '../../Redux'
import Payment from './Payment/Payment'
import { SelfServiceSettingsType } from '../../interfaces'

interface ReservationFoundProps {
    serviceCard: ServiceCardType;
    setServiceCard: (serviceCard: ServiceCardType) => void
    onBack: () => void
}

export const ReservationFound: React.FC<ReservationFoundProps> = ({ serviceCard, setServiceCard, onBack }) => {
    const locale = getLocale();
    const { t } = useTranslation();

    const reservation = serviceCard.sale ?? serviceCard.reservation
    const registerInfo = useSelector((state: RootState) => state.reservation?.registerInfo)
    const customerInfo = useSelector((state: RootState) => state.ui.customer)
    const settings: SelfServiceSettingsType = useSelector(
        (state: RootState) => state.ui.settings?.selfService ?? {},
    )
    const driveInAvailable = settings.driveInAvailable ?? false

    const [paymentStep, setPaymentStep] = useState({active: false, method: undefined})

    const handlePayment = useCallback((method: string | null) => {
        setPaymentStep({active: true, method})
    }, [])

    const backFromPayment = () => {
        setPaymentStep({active: false, method: undefined})
    }

    const date = DateTime.fromISO(
        reservation.tasks[0].event.duration.start,
        {setZone: true}
    ).setLocale(locale);
    const hasRemainingPayment = serviceCard.sale?.remainingPrice?.vatIncluded > 0;
    const hasOngoingPayment = serviceCard.sale?.hasPendingPayment ?? false;
    const registrationNumber = reservation.tasks[0].vehicle.registrationNumber;
    const isFutureReservation = date.toFormat('dd.MM.yyyy') !== DateTime.local().toFormat('dd.MM.yyyy');

    const [showSelectService, setShowSelectService] = useState<boolean>(false);
    const [showVehicleInformation, setShowVehicleInformation] = useState<boolean>(true);
    const [moveToWorkQueue, setMoveToWorkQueue] = useState<boolean>(false);

    if (showVehicleInformation) {
        return <VehicleInformation
            registrationNumber={registrationNumber}
            autoContinue
            handleContinue={() => setShowVehicleInformation(false)}
            hasOtherReservation={false}
            onBack={onBack}
        />
    }

    if (paymentStep.active === true) {
        return (
            <Payment
                serviceCard={serviceCard}
                setServiceCard={setServiceCard}
                initialPayWithCard={paymentStep.method === 'payWithCard'}
                driveIn={true}
                onBack={backFromPayment}
            />
        )
    }

    const renderFutureReservationNotice = () => {
        if (!isFutureReservation) return null

        return (
            <div className="contentWithMargin" style={{ marginTop: '2em' }}>
                <Typography variant="subtitle1" color="error">
                    {driveInAvailable
                        ? t('selfService.futureReservationNotice')
                        : t('selfService.futureReservationNoticeNoDriveIn')}
                </Typography>
            </div>
        )
    }

    const renderSignUpButton = () => {
        if (isFutureReservation && !driveInAvailable) {
            return (
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => window.location.reload()}
                >
                    {t('selfService.returnToStart')}
                </Button>
            )
        }

        return (
            <Button
                variant="contained"
                color="secondary"
                onClick={() => setMoveToWorkQueue(true)}
            >
                {t('selfService.signUp')}
            </Button>
        )
    }

    const renderSelectServiceButton = () => {
        if (isFutureReservation && !driveInAvailable) {
            return (
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => window.location.reload()}
                >
                    {t('selfService.returnToStart')}
                </Button>
            )
        }

        return (
            <Button
                variant="contained"
                color="secondary"
                onClick={() => setShowSelectService(true)}
            >
                {t('selfService.selectService')}
            </Button>
        )
    }

    const renderActionButtons = () => {
        const hasBasketItemId = Boolean(
            reservation.tasks[0]?.basket?.items?.[0]?.id,
        )

        return (
            <>
                {(customerInfo?.isInvoicingEnabled || !hasRemainingPayment) &&
                    hasBasketItemId &&
                    renderSignUpButton()}

                {!hasRemainingPayment && !hasBasketItemId &&
                    renderSelectServiceButton()
                }

                {hasRemainingPayment && !customerInfo?.isInvoicingEnabled && (
                    <>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handlePayment('payWithCard')}
                        >
                            {t('selfService.payWithCard')}
                        </Button>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => handlePayment('payOnline')}
                        >
                            {t('selfService.payOnline')}
                        </Button>
                    </>
                )}
            </>
        )
    }

    return (moveToWorkQueue === false && !showSelectService ?
        <Grid container wrap="nowrap" direction="column" justifyContent="space-between" alignItems="center" spacing={4} className={'pageMainContainer'}>
            <Grid>
            </Grid>
            <Grid justifyItems="center" style={{textAlign: 'center'}}>
                <Typography variant="h4" gutterBottom>{t('selfService.reservationFound')}</Typography>
                <Stack direction="row" spacing={4} className="contentWithMargin">
                    <div style={{width: 'fit-content'}}>
                        <Typography variant="h1" noWrap className="registrationNumber">{registrationNumber}</Typography>
                    </div>
                    <div style={{marginTop: '0.75rem', marginRight: '-1.5em', textAlign: 'start'}}>
                        <Typography variant="h5" style={{height: '1.5em', overflowY: 'hidden'}}>
                            {serviceCard.sale.tasks[0].vehicle.manufacturer?.name ?? registerInfo?.manufacturer?.name}{' '}
                            {serviceCard.sale.tasks[0].vehicle.model ?? registerInfo?.model}{' '}
                            {reservation.tasks[0].vehicle.color?.name}
                        </Typography>
                        {date.isValid &&
                            <Typography variant="subtitle1">
                                {date.toFormat(`cccc dd.MM.yyyy '${t('common.time_of_day')}' HH:mm`)}
                            </Typography>
                        }
                        {reservation.tasks[0]?.basket?.items?.length > 0 ? <Typography variant="subtitle1">
                            {reservation.tasks[0]?.basket?.items?.[0]?.name}{' '}
                            {
                                numberToCurrencyString(reservation.totalPrice?.vatIncluded ?? reservation.tasks[0]?.basket?.items?.[0]?.price?.value, 'EUR', 0)
                            }
                            {', '}
                            {hasOngoingPayment ? t('selfService.paymentOngoing') : (hasRemainingPayment ? (customerInfo?.isInvoicingEnabled ? t('selfService.paymentViaInvoice') :  t('selfService.notPayed')) : t('selfService.payed'))}
                        </Typography> : null}
                    </div>
                </Stack>
                {renderFutureReservationNotice()}
            </Grid>
            <Grid className="footerButtons">
                <Grid container wrap="nowrap" spacing={4} justifyContent="space-between" alignItems="end">
                    <Grid>
                        <BackButton onBack={onBack} />
                    </Grid>
                    <Grid>
                        <Stack direction="row" spacing={2}>
                            {renderActionButtons()}
                        </Stack>
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
        :
        (showSelectService ?
            <SelectService
                isDriveIn={false}
                campaignCode={null}
                afterInspectionProductId={null}
                handlePayment={handlePayment}
                serviceCard={serviceCard}
                setServiceCard={setServiceCard}
                setSelectedService={() => {}}
                onBack={onBack}
                isMissingService={true}
            />
            :
            <MoveToWorkQueue serviceCard={serviceCard}/>
        )
    )
};
